import { useEffect, useRef, useCallback } from 'react';
import { slideUpThreshold, closeThreshold } from './drawerConsts';
function getOpacityFromTranslate(translate, windowHeight) {
    var result = Math.min((translate - windowHeight) / windowHeight);
    return Math.abs(result).toFixed(3);
}
var useDrawerBehavior = function (containerRef, backdropRef, contentRef, isOpen, onOpenUpdate) {
    var windowHeight = useRef(0);
    if (windowHeight.current === 0) {
        windowHeight.current = window.innerHeight;
    }
    var translateRef = useRef(0);
    var mouseY = useRef(null);
    var isClickedRef = useRef(false);
    var scheduleTranslateUpdate = useCallback(function (translate) {
        translateRef.current = translate;
        requestAnimationFrame(function () {
            if (containerRef.current && backdropRef.current) {
                var hasTranslate = translate !== undefined && translate !== null;
                var transform = hasTranslate ? "translateY(" + translate + "px)" : '';
                containerRef.current.style.transitionDuration = isClickedRef.current
                    ? 'initial'
                    : '';
                containerRef.current.style.transform = transform;
                var opacity = hasTranslate
                    ? getOpacityFromTranslate(translate, windowHeight.current)
                    : '';
                backdropRef.current.style.opacity = opacity;
                backdropRef.current.style.transition = hasTranslate ? 'initial' : '';
            }
        });
    }, []);
    var close = useCallback(function () {
        var closed = 'translateY(100%)';
        requestAnimationFrame(function () {
            if (containerRef.current) {
                containerRef.current.style.transitionDuration = '';
                containerRef.current.style.transform = closed;
                translateRef.current = windowHeight.current;
            }
        });
        var reportClose = function (e) {
            e.preventDefault();
            if (containerRef.current.style.transform === closed) {
                onOpenUpdate(false);
            }
        };
        var transitions = [
            'webkitTransitionEnd',
            'transitionend',
            'msTransitionEnd',
            'oTransitionEnd',
            'transitionEnd',
        ];
        transitions.forEach(function (t) {
            return containerRef.current.addEventListener(t, reportClose);
        });
        return function () {
            return transitions.forEach(function (t) {
                return containerRef.current.removeEventListener(t, reportClose);
            });
        };
    }, []);
    var onBackdropClick = useCallback(function (e) {
        e.preventDefault();
        var container = containerRef.current;
        if (container && !container.contains(e.target)) {
            close();
        }
    }, []);
    useEffect(function () {
        var onResize = function () {
            windowHeight.current = window.innerHeight;
        };
        window.addEventListener('resize', onResize);
        return function () {
            window.removeEventListener('resize', onResize);
        };
    }, []);
    useEffect(function () {
        scheduleTranslateUpdate();
    }, [isOpen, scheduleTranslateUpdate]);
    useEffect(function () {
        var unify = function (e) { return (e.changedTouches ? e.changedTouches[0] : e); };
        var handleClick = function (e) {
            var newY = unify(e).clientY;
            mouseY.current = newY;
            isClickedRef.current = true;
            scheduleTranslateUpdate();
        };
        var handleMove = function (e) {
            if (isClickedRef.current && mouseY.current) {
                var newY = unify(e).clientY;
                var del = Math.round(newY - mouseY.current);
                mouseY.current = newY;
                var baseValue = translateRef.current || 0;
                var nextPos = baseValue + del;
                var inRange = nextPos > -slideUpThreshold;
                if (contentRef && contentRef.current) {
                    var onTop = contentRef.current.scrollTop === 0;
                    var onBottom = contentRef.current.scrollTop ===
                        contentRef.current.scrollHeight - contentRef.current.clientHeight;
                    var swipeDown = nextPos > 0;
                    if (onTop && swipeDown) {
                        scheduleTranslateUpdate(nextPos);
                    }
                    else if (onBottom && !swipeDown && inRange) {
                        scheduleTranslateUpdate(nextPos);
                    }
                }
                else {
                    if (inRange) {
                        scheduleTranslateUpdate(nextPos);
                    }
                }
            }
        };
        var handleRelease = function (e) {
            isClickedRef.current = false;
            if (translateRef.current >=
                containerRef.current.clientHeight * closeThreshold)
                close();
            else
                scheduleTranslateUpdate();
        };
        var events = [
            [containerRef.current, 'mousedown', handleClick],
            [containerRef.current, 'touchstart', handleClick],
            [containerRef.current, 'mousemove', handleMove],
            [containerRef.current, 'touchmove', handleMove],
            // register release over entire window
            [window, 'mouseup', handleRelease],
            [window, 'touchend', handleRelease],
        ];
        events.forEach(function (_a) {
            var target = _a[0], e = _a[1], listener = _a[2];
            return target.addEventListener(e, listener, true);
        });
        return function () {
            events.forEach(function (_a) {
                var target = _a[0], e = _a[1], listener = _a[2];
                return target.removeEventListener(e, listener, true);
            });
        };
    }, [close, scheduleTranslateUpdate]);
    return { close: close, onBackdropClick: onBackdropClick };
};
export default useDrawerBehavior;
